import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CONTENT_CARD, GROUP, FORM, MENU, TEXT } from "constants/widgetTypes";

import {
  toggleEditMode,
  updateWidgetProperty,
  updateWidgetContent,
  updateEditorThemes,
  updateWidgetLayout,
  updateWidgetActions,
  actionGroup,
} from "actions/editor";
import {
  calculateBoundsForElement,
  getParentContainerOfType, getWidgetType,
} from "components/Editor/helper/widget";

import _ from "lodash";
import cx from "classnames";
import * as DOMPurify from "dompurify";

import { Frame } from "scenejs";
import { Editor } from "@tinymce/tinymce-react";

import { EditorContext } from "../../index";
import {
  calculateFontSizeForMobile,
  generateNodeFromSelection,
  getActionsFromHTML,
  getRootBlockFromNode,
  getRootBlockFromSelection,
  getStylesForNode,
  handleEmptyContentInEditor,
  getThemeFromNode,
  getFormatFromTheme,
  isAllContentSelected,
  updateContentForVariables,
  isEditorContentFullySelected,
} from "../../helper/text";

import { Button, Dropdown, Form } from "react-bootstrap";
import { Property } from "../../components/properties/";
import { SuccessToast, WarningToast } from "components/Toast";
import { toast } from "react-toastify";
import {
  AnimationProperty,
  Animation,
} from "components/Editor/components/animations";
import Tooltip from "components/Tooltip";
import { isMacOs } from 'react-device-detect';

import { Font } from "../../components/properties/fonts";
import { colorToString } from "components/Editor/helper/color";
import Actions from "components/Editor/components/actions";
import { CreateVariableForm, VariableProperty, VaribaleDropdownMenu } from "components/Editor/components/variables";
import { createVariable } from "actions/variables";
import { LiveElementCreationProperty } from "../../components/liveElements";

import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
} from "@fortawesome/free-solid-svg-icons";

import { extractBackgroundColorFromElement } from "components/Editor/helper/color";
import { isWidgetLocked, useAllowCreateLiveElement, useSelectedWidgetSelector } from "components/Editor/hooks/useEditorSession";

import { associateFontsWithWigdet } from "actions/fonts";
import { useFontsFromHtml } from "components/Font";
import { Fonts } from "@paperflite-marketing/cleverstory-commons";

import { PageWidgetContext } from "../page";
import StoryboardContext from "components/Storyboard/context";

import { BrandComponentProperty } from "components/Editor/components/brandElements";
import userRoleService from "services/userRoles.service";
import styles from "./text.module.scss";
import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS } from "constants/storyboardType";

const TextWidgetViewComponent = function ({
  className,
  style,
  widgetProps,
  widgetRef,
}) {
  const dispatch = useDispatch();
  const ref = widgetRef;
  const editorRef = React.useRef(null);
  const isInternalUpdate = useRef(false);
  const associatedFontsRef = useRef([]);

  const pageWidgetContext = React.useContext(PageWidgetContext);
  const editorContext = React.useContext(EditorContext);
  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, "type");
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");
  let content = _.get(widgetProps, "content");
  const contentRef = useRef(content);
  const variables = _.get(widgetProps, `properties.desktop.variables`);
  const variablesData = useSelector((state) => _.get(state, `variables.items`, []));
  const parent = _.get(widgetProps, 'parent');

  if (content && content.includes('data-variable-id')) {
    content = updateContentForVariables(content, variables, variablesData);
  }

  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );
  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);
  const layoutMobile = _.get(widgetProps, `properties.mobile.layout`);
  const disableOptimize = _.get(widgetProps, `properties.mobile.disableOptimize`);
  const parentType = getWidgetType(editor, parent);
  const selectedWidgets = useSelectedWidgetSelector(editor);

  const multipleTextWidgetsSelected = 
  selectedWidgets?.length > 1 && 
  selectedWidgets?.every(widget => widget.type === TEXT);

  const [textConfig, setTextConfig] = useState({
    content
  });
  const [mobileLayoutConfig, setMobileLayoutConfig] = useState(null);
  const [desktopLayoutConfig, setDesktopLayoutConfig] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState(null);
  const [textClickPos, setTextClickPos] = React.useState(null);
  const editMode = useSelector(state => {
    return _.get(state, `${editor}.present.editMode`) === id
  });

  const editViewType = useSelector((state) => {
    return _.get(state, `${editor}.present.editView`);
  });

  const batchEdit = useSelector((state) => {
    return _.get(state, `${editor}.present.batchEdit`);
  });

  const selected = useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`);
    if (_.size(selected) != 1) {
      return false;
    }

    return _.includes(selected, id);
  });
  const allWidgets = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.widgets`));

  const isLocked = isWidgetLocked(allWidgets, widgetProps, breakpoint);
  const isLockedRef = React.useRef(isLocked);

  useEffect(() => {
    isLockedRef.current = isLocked;
  
    const tiny = editorRef.current?.editor || (editorRef.current && editorRef.current);
    if (tiny && tiny.getBody) {
      const body = tiny.getBody();
      if (body) body.setAttribute("contenteditable", isLocked ? "false" : "true");
    }
  }, [isLocked]);

  const associatedFonts = useFontsFromHtml(content);
  Fonts.useFont(_.get(widgetProps, "fonts") || []);

  useEffect(() => {
    if (editorRef.current && editorRef.current.editor) {
      if (editMode && editorRef.current.editor.initialized) {
        editorRef.current.editor.focus();
        editorContext.setEditingState(id, true);
      } else {
        const selection = editorRef.current.editor.selection;
        if (selection) {
          selection.getSel().removeAllRanges();
        }

        editorRef?.current?.elementRef?.current?.blur();
        setTimeout(() => {
          if (!editorRef?.current?.editor?.hasFocus?.()) {
            editorContext.setEditingState(id, false);
          }
        }, 300);
      }
    }
  }, [editMode]);

  useEffect(() => {
    const fallbackFontSize = _.get(widgetProps, 'properties.desktop.fontSize');
    if (!fallbackFontSize || !content) return;
  
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
  
    let changed = false;
    const blocks = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
    blocks.forEach((block) => {
      if (!block.style.fontSize) {
        block.style.fontSize = `${fallbackFontSize}px`;
        changed = true;
      }
    });
  
    if (changed) {
      const updatedContent = tempDiv.innerHTML;
      isInternalUpdate.current = true;
      contentRef.current = updatedContent;
      setTextConfig({ content: updatedContent });
      dispatch(updateWidgetContent(editor, id, updatedContent));
    }
  }, []);

  useEffect(() => {
    if (selectedWidgets?.length == 0 && batchEdit) {
      dispatch(toggleEditMode(editor, id, false, null, false));
    }
  },[batchEdit, selectedWidgets])

  useEffect(() => {
    if (!isInternalUpdate.current) {
      if (content !== contentRef.current) {
        setTextConfig((p) => ({
          ...p,
          content
        }));
      }
    }

    contentRef.current = content;
    isInternalUpdate.current = false;
  }, [content]);

  useEffect(() => {
    if (editMode) {
      dispatch(toggleEditMode(editor, id, false));
    }
  }, [breakpoint]);

  useEffect(() => {
    const fontsChanged = !_.isEqual(
      _.sortBy(associatedFonts),
      _.sortBy(associatedFontsRef.current)
    );
    
    if (fontsChanged) {
      dispatch(associateFontsWithWigdet(editor, id, associatedFonts));
      associatedFontsRef.current = associatedFonts;
    }
  }, [associatedFonts]);

  useEffect(() => {
    const handleText = () => {
      const {content: newContent} = textConfig || {};
      
      if (editor && id && newContent && newContent !== contentRef.current) {
        isInternalUpdate.current = true;
        actionGroup.start();
        dispatch(updateWidgetContent(editor, id, newContent));
        actionGroup.end();
        contentRef.current = newContent;
      }
    }

    const handler = setTimeout(handleText, 500);
    return () => clearTimeout(handler);
  }, [textConfig]);

  const useLayoutUpdater = (layoutConfig, breakpoint) => {
    useEffect(() => {
      if (!layoutConfig) return;
  
      const handleLayout = () => {
        actionGroup.start();
        dispatch(updateWidgetLayout(editor, id, breakpoint, layoutConfig));
        actionGroup.end();
      };
  
      const handler = setTimeout(handleLayout, 500);
      return () => clearTimeout(handler);
    }, [layoutConfig]);
  };

  useLayoutUpdater(mobileLayoutConfig, 'mobile');
  useLayoutUpdater(desktopLayoutConfig, 'desktop');

  const handleContentClicked = (e) => {
    if (isLocked) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (isDragging) {
      setIsDragging(false);
      return;
    }

    if (selected && !editMode) {
      e.stopPropagation();
      setTextClickPos({ x: e.clientX, y: e.clientY });
      dispatch(toggleEditMode(editor, id, true));
      return;
    }

    if (selected && editMode) {
      if (editorRef.current) {
        if (editorRef.current.editor.hasFocus()) {
          return;
        }
        editorRef.current.editor.focus();
        const selection = editorRef.current.editor.selection;
        if (selection) {
          selection.placeCaretAt(e.clientX, e.clientY);
        }
      }
    }
  };

  const onChange = (e) => {
    const updatedContent = editorRef.current.editor.getContent();
    if (updatedContent === textConfig.content) {
      return;
    }

    if (updatedContent != content && PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
      pageWidgetContext.onResize();
    }

    const textElement = document.getElementById(`${id}`);
    const textRect = textElement?.getBoundingClientRect();
    const widthCondition = _.includes([FORM, MENU], parentType)

    if (!textRect) return;

    const divider = textRect?.height >= 30 ? 2 : 
                    textRect?.height >= 22 ? 1.5 : 
                    textRect?.height >= 12 ? 0.8 : 0.4;

    const isAutoWidth = layout.width === 'auto';
    const textWidth = textRect?.width || 0;
    const scaledWidth = textWidth / divider;
    setTextConfig({ content: updatedContent });

    let updatedMobileLayout = { ...layoutMobile };
    let mobileWidth = parseFloat(updatedMobileLayout.width) || 0;

    if (isAutoWidth) {
      const exceedsMaxWidth = textWidth >= 800 || scaledWidth >= 335;
      updatedMobileLayout.width = widthCondition ? '295px' : (exceedsMaxWidth ? '335px' : 'auto');
    } else if (mobileWidth > 365) {
      const isNarrowText = scaledWidth <= 335;
      updatedMobileLayout.width = widthCondition ? '295px' : (isNarrowText ? `${scaledWidth}px` : '335px');
    }
    
    setMobileLayoutConfig(updatedMobileLayout);

    if (isAutoWidth && textWidth >= 800) {
      const updatedLayout = {
        ...layout,
        width: "800px",
      };
      setDesktopLayoutConfig(updatedLayout);
    }
    
  };

  const cleanFigmaContent = (content) => {
    const isFigmaContent = content.includes('data-metadata="<!--(figmeta)') || 
                           content.includes('data-buffer="<!--(figma)')
                           
    if (!isFigmaContent) {
      return content;
    }
  
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    let cleanText = tempDiv.textContent || tempDiv.innerText || '';
    
    cleanText = cleanText
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')   
      .replace(/^\s+|\s+$/g, '') 
      .replace(/\n\s*\n/g, '\n') 
      .trim();
  
    const paragraphs = cleanText.split('\n').filter(p => p.trim());
    
    if (paragraphs.length === 0) {
      return '<p></p>';
    }
    
    if (paragraphs.length === 1) {
      return `<p>${paragraphs[0]}</p>`;
    }
    
    return paragraphs.map(p => `<p>${p}</p>`).join('');
  };

  const fields = [{ name: "Text", path: "content", type: "TEXT" }];

  var widgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint),
  };

  const handleMouseDown = (e) => {
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setIsDragging(false);
  };
  
  const handleMouseMove = (e) => {
    if (dragStartPos) {
      const deltaX = Math.abs(e.clientX - dragStartPos.x);
      const deltaY = Math.abs(e.clientY - dragStartPos.y);
      
      if (deltaX > 5 || deltaY > 5) {
        setIsDragging(true);
      }
    }
  };
  
  const handleMouseUp = (e) => {
    setTimeout(() => {
      setDragStartPos(null);
      setIsDragging(false);
    }, 10);
  };
  

  return (
    <div
      id={id}
      ref={ref}
      className={cx(className, styles.text, "widget", {
        [styles.selected]: selected,
      })}
      style={widgetStyle}
      data-container={id}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {breakpoint === "desktop" && selected && editMode && (
        <Animation widget={widgetProps}>
          <Editor
            ref={editorRef}
            id={`${id}_editor`}
            tinymceScriptSrc="/tinymce-variable/plugin.min.js"
            className={styles.editor}
            key={id}
            value={textConfig.content}
            inline={true}
            init={{
              external_plugins: {
                variable: "/tinymce-variable/plugin.js",
              },
              plugins: ["lists", "autoresize", "variable", "noneditable"],
              noneditable_class: "mce-variable" || "mceNonEditable",
              menubar: false,
              toolbar: false,
              quickbars_insert_toolbar: false,
              quickbars_selection_toolbar: false,
              browser_spellcheck: true,
              autoresize_bottom_margin: 50,
              autoresize_overflow_padding: 50,
              min_height: 100,
              valid_elements: "*[*]",
              content_css: [],
              end_container_on_empty_block: true,
              extended_valid_elements: "span[*],var[*]",
              formats: {
                fontsize_parent: {
                  selector: "h1,h2,h3,h4,h5,h6,p,li,td,th,div",
                  inline: "h1",
                  styles: { fontSize: "%value" },
                },
                bold: [{ inline: "span", styles: { fontWeight: "bold" } }],
                italic: [{ inline: "span", styles: { fontStyle: "italic" } }],
                superscript: [{ inline: "sup" }],
                subscript: [{ inline: "sub" }],
                strikethrough: [{ inline: "s" }],
              },
              variable_class: "mce-variable",
              paste_preprocess: function (plugin, args) {
                const pasted = args.content;
                if (!pasted) {
                  return;
                }
                let processedContent = cleanFigmaContent(pasted);

                // Configure DOMPurify
                DOMPurify.addHook("uponSanitizeElement", function (node, data) {
                  // Allow span with class "variable-wrapper" or "variable-placeholder"
                  if (
                    data.tagName === "span" &&
                    (node.classList.contains("variable-wrapper") ||
                      node.classList.contains("mce-variable" || "mceNonEditable"))
                  ) {
                    data.keepContent = true; // Do not remove children
                    return;
                  }
                });

                DOMPurify.addHook(
                  "uponSanitizeAttribute",
                  function (node, data) {
                    // Allow specific data attributes for variable-placeholder
                    if (
                      node.classList.contains("mce-variable" || "mceNonEditable") &&
                      (data.attrName === "data-variable" ||
                        data.attrName === "data-variable-id" ||
                        data.attrName === "contenteditable")
                    ) {
                      return;
                    }
                  }
                );

                var processed = DOMPurify.sanitize(processedContent, {
                  ALLOWED_TAGS: ["br", "ul", "li", "ol", "span"],
                  ALLOWED_ATTR: [
                    "class",
                    "style",
                    "variable-wrapper",
                    "data-variable",
                    "data-variable-id",
                    "contenteditable",
                  ],
                });
                const span = generateNodeFromSelection(
                  editorRef.current.editor
                );
                if (!!span) {
                  span.innerHTML = processed;
                  processed = span.outerHTML;
                }

                args.content = processed;
              },
              setup: (editor) => {
                editorRef.current = editor;
                editor.on('init', () => {
                  setTimeout(() => {
                    const body = editor.getBody();
                    editor.focus();
                    const selection = editor.selection;
                    
                    if (textClickPos) {
                      selection.placeCaretAt(textClickPos.x, textClickPos.y);
                      setTextClickPos(null);
                    } else {
                      if (body && body.firstChild) {
                        selection.select(body, true);
                        selection.collapse(true);
                      } else {
                        selection.setCursorLocation(body, 0);
                      }
                    }
                  }, 0);
                });
                
                editorContext.registerEditor(id, editor);

                editor.on("remove", () => {
                  editorContext.unregisterEditor(id);
                });

                editor.on("keydown", async function (e) {
                  const modifierKey = isMacOs ? e.metaKey : e.ctrlKey;
                  const selection = editor.selection;
                  const content = editor.getContent({ format: 'text' });
                  const isDeleteKey = e.keyCode === 8 || e.keyCode === 46; // Backspace or Delete
                  const isAllSelected = isEditorContentFullySelected(editor);
                
                  // ---- CASE 1: Paste as Plain Text (Ctrl+Shift+V or Cmd+Shift+V) ----
                  if (modifierKey && e.shiftKey && (e.key === 'v' || e.key === 'V')) {
                    e.preventDefault();
                    e.stopPropagation();
                
                    try {
                      const text = await navigator.clipboard.readText();
                
                      editor.undoManager.transact(() => {
                        // Remove inline formatting
                        editor.formatter.remove('bold');
                        editor.formatter.remove('italic');
                        editor.formatter.remove('underline');
                        editor.formatter.remove('strikethrough');
                        editor.formatter.remove('superscript');
                        editor.formatter.remove('subscript');
                
                        // Remove color formatting
                        editor.execCommand('mceRemoveTextcolor', 'forecolor');
                        editor.execCommand('mceRemoveTextcolor', 'hilitecolor');
                
                        // Remove any remaining styles
                        editor.execCommand('RemoveFormat');
                
                        // Insert plain text
                        editor.execCommand('mceInsertContent', false, text);
                      });
                    } catch (err) {
                      console.error("Clipboard paste as plain text failed:", err);
                    }
                  }
                
                  // ---- CASE 2: Delete All Content but Keep Styles ----
                  else if (isDeleteKey && isAllSelected && content.trim().length > 0) {
                    e.preventDefault();
                
                    // Get current node with styles
                    const currentNode = selection.getNode();
                    const rootBlock = getRootBlockFromNode(currentNode);
                
                    if (rootBlock) {
                      // Clone the root block structure
                      const clonedBlock = rootBlock.cloneNode(true);
                
                      // Recursive function to clear text but preserve spans/styles
                      const clearTextKeepStyles = (node) => {
                        Array.from(node.childNodes).forEach(child => {
                          if (child.nodeType === Node.TEXT_NODE) {
                            child.nodeValue = '';
                          } else if (child.nodeType === Node.ELEMENT_NODE) {
                            if (child.nodeName === 'BR') {
                              child.remove();
                            } else {
                              clearTextKeepStyles(child);
                            }
                          }
                        });
                
                        // Ensure at least one span has a zero-width space placeholder
                        if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'SPAN') {
                          if (!node.textContent || node.textContent.trim() === '') {
                            node.innerHTML = '\u200B';
                          }
                        }
                      };
                
                      clearTextKeepStyles(clonedBlock);
                
                      // Replace content with cleared styled structure
                      editor.setContent(clonedBlock.outerHTML);
                
                      // Place cursor at beginning of first span
                      const body = editor.getBody();
                      const firstSpan = body.querySelector('span');
                      if (firstSpan) {
                        selection.select(firstSpan, true);
                        selection.collapse(true);
                      }
                
                      // Trigger change event
                      editor.fire('change');
                    }
                  }
                });                

                editor.on("PostRender", () => {
                  const body = editor.getBody();

                  if (isLockedRef.current) {
                    body?.setAttribute('contenteditable', 'false');
                  } else {
                    body?.setAttribute('contenteditable', 'true');
                  }

                  const frame =
                    editorContext.editorSession.moveableData.createFrame(
                      widgetRef.current,
                      layout
                    );

                  editorContext.editorSession.moveableData.render(
                    widgetRef.current,
                    frame
                  );

                  if (editorContext.editorSession.moveableManager.current) {
                    editorContext.editorSession.moveableManager.current.updateRect();
                  }
                  
                });

                editor.on("selectionChange", function (e) {});

                editor.on("NewBlock", function (e) {
                  const newBlock = e.newBlock;
                  const previousSibling = newBlock.previousSibling;

                  if (newBlock && newBlock.nodeName.toLowerCase() === "li") {
                    return;
                  }

                  var fontFormat = "p";
                  const previousRootBlock = getRootBlockFromNode(previousSibling);

                  if (previousRootBlock) {
                    fontFormat = previousRootBlock.nodeName.toLowerCase();
                  }

                  const rootBlock = editor.dom.create(fontFormat);

                  const previousFontSize = previousRootBlock?.style?.fontSize;
                  const fallbackFontSize = previousFontSize 
                    || (_.get(widgetProps, 'properties.desktop.fontSize') 
                        ? `${_.get(widgetProps, 'properties.desktop.fontSize')}px` 
                        : null);
                
                  if (fallbackFontSize) {
                    rootBlock.style.fontSize = fallbackFontSize;
                  }
                  
                  editor.dom.replace(rootBlock, newBlock, true);
                });

                editor.on("BeforeSetContent", function (e) {
                  if (e.content === "" && !e.pasted) {
                    const updatedContent = handleEmptyContentInEditor(
                      editorRef.current.editor,
                      e.target.bodyElement
                    );
                    e.content = updatedContent;
                    return;
                  }
                });

                editor.on("drop", (e) => {
                  e.preventDefault();
                });
              },
            }}
            onClick={handleContentClicked}
            onEditorChange={onChange}
          />
        </Animation>
      )}

      {breakpoint === "desktop" && selected && !editMode && (
        <Animation widget={widgetProps}>
          <TextDesktopView props={{
            ...widgetProps,
            handleContentClicked,
            content: textConfig.content,
          }} />
        </Animation>
      )}

      {breakpoint === "desktop" && !selected && (
        <Animation widget={widgetProps}>
          <TextDesktopView props={{
            ...widgetProps,
            content: textConfig.content,
          }} />
        </Animation>
      )}

      {breakpoint === "mobile" && (
        <Animation widget={widgetProps}>
          <TextMobileView props={{
            ...widgetProps,
            content: textConfig.content,
          }} />
        </Animation>
      )}

      {editMode &&
        editViewType === "text" &&
        breakpoint === "desktop" &&
        editorRef.current &&
        editorRef.current.editor && (
          <TextStyleEditor
            key={`style_editor_${id}`}
            id={id}
          setTextConfig={setTextConfig}
            editorSession={editor}
            textEditor={editorRef.current.editor}
            {...widgetProps}
          />
        )}

      { batchEdit &&
        editViewType === "text" &&
        breakpoint === "desktop" && 
        multipleTextWidgetsSelected &&
        selectedWidgets[0]?.uid === id &&(
          <MultiTextStyleEditor
            editorSession={editor}
            selectedWidgets={selectedWidgets}
          />
      )}

      { batchEdit &&
        editViewType === "text" &&
        breakpoint === "mobile" && 
        selectedWidgets[0]?.uid === id &&(  
          <MultiMobileTextStyleEditor
            editorSession={editor}
            selectedWidgets={selectedWidgets}
          />
      )}

      {editMode &&
        editViewType === "variable" &&
        breakpoint === "desktop" &&
        editorRef.current &&
        editorRef.current.editor && (
          <TextVariableDialogue
            editorSession={editor}
            textEditor={editorRef.current.editor}
            widget={widgetProps}
            disabled={editorRef.current.editor.selection?.isCollapsed()}
            onCancel={() =>
              dispatch(toggleEditMode(editor, widgetProps.uid, false, null))
            }
            fields={fields}
          />
        )}

      {editMode && breakpoint === "mobile" && !batchEdit &&(
        <TextMobileStyleEditor
          key={`style_editor_${id}`}
          id={id}
          editorSession={editor}
          {...widgetProps}
        />
      )}
    </div>
  );
};

const textWidgetEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

const TextWidgetView = React.memo(TextWidgetViewComponent, textWidgetEquals);

const applyLineHeight = (content, lineSpacing) => {
  const root = document.createElement("div");
  root.innerHTML = content;
  root.querySelectorAll("[style*='font-size']").forEach(el => {
    const size = parseFloat(el.style.fontSize);
    if (size) el.style.lineHeight = `${Math.floor(size * lineSpacing)}px`;
  });

  return root.innerHTML;
};

const TextDesktopView = ({props}) => {
  const content = _.get(props, "content");
  const handleContentClicked = _.get(props, "handleContentClicked");
  const ref = React.useRef(null);
  const lineSpacing = _.get(props, "properties.desktop.lineSpacing") || 1.2;

  return (
    <div
      ref={ref}
      className={cx(styles.inner)}
      onClick={handleContentClicked}
      dangerouslySetInnerHTML={{ __html: applyLineHeight(content, lineSpacing) }}
    />
  );
};

const TextMobileView = ({props}) => {
  const id = _.get(props, "uid");
  const value = _.get(props, "content");

  const desktopFontSize = _.get(props, "properties.desktop.fontSize");
  const fontSize = _.get(props, "properties.mobile.fontSize");
  const lineSpacing = _.get(props, "properties.mobile.lineSpacing") || 1.2;
  const letterSpacing = _.get(props, "properties.mobile.letterSpacing") || 0;

  const processContent = (content) => {
    var root = document.createElement("div");
    root.innerHTML = (content || "").trim();

    const safeDeskopFontSize = desktopFontSize || 16;
    const ratio = fontSize / safeDeskopFontSize;

    Array.from(root.children).forEach((child) => {
      const existingFontSize = parseFloat(child.style.fontSize) || safeDeskopFontSize;
      child.style.fontSize = `${existingFontSize * ratio}px`;

      Array.from(child.children).forEach((childNode) => {
        processChildNodes(childNode, ratio);
      });
    });

    return applyLineHeight(root.innerHTML, lineSpacing);
  };

  const processChildNodes = (node, ratio) => {
    if (!node || node.nodeType != 1) {
      return;
    }

    var fontValue = node.style.fontSize;
    if (fontValue) {
      fontValue = parseFloat(fontValue);
      var newValue = fontValue * ratio;
      newValue = Math.max(6, newValue);
      node.style.fontSize = `${newValue}px`;
    }

    Array.from(node.children).forEach((child) => {
      processChildNodes(child, ratio);
    });
  };

  return (
    <div
      className={styles.inner}
      dangerouslySetInnerHTML={{ __html: processContent(value) }}
    />
  );
};

const TextWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const id = _.get(props, "uid");

  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  useEffect(() => {
    setView(null);
  },[id])

  const allowVariable =
    useSelector((state) =>
      _.get(state, `${editorSession}.present.entityType`)
    ) === "PAGE";


  const template = _.get(props, "template.widget") != null;

  const handleEditMode = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, true, view));
    setView(null);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  };

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  };

  return (
    <>
      <Property.Action
        className={styles.textButton}
        onClick={handleEditMode.bind(this, "text")}
      >
        Format text
      </Property.Action>
      <Property.Divider />
      <Property.Action
        className={styles.iconButton}
        tooltip="Animation"
        onClick={() => changeView("animation")}
      >
        <i className={cx("cleverstory-animations")} />
      </Property.Action>
      {!template && allowLiveElement && (
        <Property.Action
          className={styles.iconButton}
          tooltip="Live Element"
          onClick={() => changeView("liveElement")}
        >
          <i className={"cleverstory-live-element"} />
        </Property.Action>
      )}
      {allowVariable && (
        <Property.Action
            className={styles.iconButton}
            tooltip="AI Personalization"
            onClick={() => changeView("personalization")}
        >
            <i className={"cleverstory-personalization-icon"} />
        </Property.Action>
      )}
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }
      {allowVariable && (
        <Property.Action
          className={styles.iconButton}
          tooltip="Make Variable"
          onClick={handleEditMode.bind(this, "variable")}
        >
          <i className={"cleverstory-variable"} data-track-event="Mixpanel" data-track-props="Make Variable" data-widget-type="Text"/>
        </Property.Action>
      )}

      {!editMode && view === "animation" && (
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup();
          }}
        />
      )}
      {view === "personalization" &&
        <TextPersonalizationSettings
          uid={props.uid}
          editor={editorSession}
          widget={props}
          onCancel={()=>{
            closePopup()
          }}
        />
      }
      {view === "liveElement" && (
        <LiveElementCreationProperty elements={[props]} onCancel={closePopup} />
      )}
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'SECTIONS'} widgets={[props]} onCancel={() => closePopup()}/>
      }
    </>
  );
};

const TextWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);

  const { editorSession, uid, ...rest } = props;
  const [view, setView] = useState(null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const template = _.get(props, "template.widget") != null;
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const fontSize = useSelector((state) =>
    _.get(rest, `properties.mobile.fontSize`)
  );

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, uid, true));
  };

  const increaseFontSize = () => {
    var value = Math.min(Math.floor(fontSize) + 1, 150);

    dispatch(
      updateWidgetProperty(editorSession, uid, "mobile", {
        fontSize: value,
      })
    );
  };

  const decreaseFontSize = () => {
    var value = Math.max(Math.floor(fontSize) - 1, 6);

    dispatch(
      updateWidgetProperty(editorSession, uid, "mobile", {
        fontSize: value,
      })
    );
  };

  let widget = {
    uid: uid,
    ...rest,
  };

  return (
    <>
      <Property.Action
        className={styles.iconButton}
        tooltip="Increase Size"
        onClick={increaseFontSize}
      >
        <i className={cx("cleverstory-text-maximize")} />
      </Property.Action>
      <Property.Action
        className={styles.iconButton}
        tooltip="Decrease Size"
        onClick={decreaseFontSize}
      >
        <i className={cx("cleverstory-text-minimize")} />
      </Property.Action>
      <Property.Divider />
      <Property.Action
        className={styles.iconButton}
        tooltip="Animation"
        onClick={() => setView("animations")}
      >
        <i className={cx("cleverstory-animations")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Setting' onClick={handleEditMode}>
        <i className={cx("cleverstory-settings", styles.settingsIcon)} />
      </Property.Action>

      {!template && allowLiveElement && (
        <Property.Action
          className={styles.iconButton}
          tooltip="Live Element"
          onClick={() => setView("liveElement")}
        >
          <i className={"cleverstory-live-element"} />
        </Property.Action>
      )}
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => setView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      <Property.Divider />

      {view === "animations" && (
        <AnimationProperty
          ids={[uid]}
          editorContext={editorContext}
          editor={editorSession}
          widgets={[widget]}
          onCancel={() => {
            setView(null);
          }}
        />
      )}

      {view === "liveElement" && (
        <LiveElementCreationProperty
          elements={[props]}
          onCancel={() => setView(null)}
        />
      )}
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'SECTIONS'} widgets={[props]} onCancel={() => setView(null)} />
      }
    </>
  );
};

const TextWidget = React.forwardRef((props, ref) => {
  return <TextWidgetView {...props} ref={ref} />;
});

TextWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === "desktop" && <TextWidgetDesktopAction {...props} />}
      {breakpoint === "mobile" && (
        <TextWidgetMobileAction editorSession={editorSession} {...props} />
      )}
    </>
  );
};

function tokensToHtmlWithChips(tokenizedText = "", variables = []) {
  if (!tokenizedText) return "";

  return tokenizedText.replace(/{{\s*([0-9A-Za-z_-]+)\s*}}/g, (m, id) => {
    const v = variables.find((x) => String(x.id) === String(id));

    if (!v) return "";

    const label = v.name || v.label || "";

    // Minimal escaping
    const escLabel = label
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    return `<span class="variable-chip" contenteditable="false" data-id="${String(
      id
    )}">{${escLabel}}</span>`;
  });
}

/**
 * Convert editor HTML with chips to tokenized text: replace chips with {{id}}
 * Also return array of ids used.
 */
function htmlWithChipsToTokensAndIds(html = "") {
  if (!html) return { text: "", ids: [] };

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const chips = doc.querySelectorAll(".variable-chip");
  const ids = [];

  chips.forEach((chip) => {
    const id = chip.getAttribute("data-id");
    if (id != null) {
      ids.push(String(id));

      // Replace chip element with {{id}}
      const tokenText = `{{${String(id)}}}`;
      const tokenNode = doc.createTextNode(tokenText);
      chip.replaceWith(tokenNode);
    }
  });

  const out = doc.body.textContent
    .replace(/\u00A0/g, " ") // convert &nbsp; to space
    .trim();

  return { text: out, ids: _.uniq(ids) };
}


const TextPersonalizationSettings = ({uid, widget, onCancel }) => {
  const dispatch = useDispatch();
  const editorRef = React.useRef(null);
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [initialTokenizedContent, setInitialTokenizedContent] = useState("");
  const [editorHtml, setEditorHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const pageId = editorSession.split("_")[1];
  const llmProperty = _.get(widget, "properties.desktop.llmProperty", {});
  const storyboard = React.useContext(StoryboardContext);

  const page = useSelector((state) =>
    _.get(state, `pages.page.${pageId}.item`, [])
  );

  const variablesData = useSelector((state) =>
    _.get(state, "variables.items", [])
  );
  const customFormFields = useSelector((state) =>
    _.get(state, "personalization.formFields.items", [])
  );
  const fields = [{ name: "Text", path: "content", type: "TEXT" }];

  useEffect(() => {
    if (variablesData && variablesData.length > 0) {
      setItems(variablesData);
    }
  }, [variablesData]);


  // If you have tokenized content when opening, convert to HTML chips
  useEffect(() => {
    if (!initialTokenizedContent) {
      // empty editor
      setEditorHtml("");
      return;
    }
    const html = tokensToHtmlWithChips(initialTokenizedContent, [...items, ...customFormFields]);
    setEditorHtml(html);
  }, [initialTokenizedContent, items]);

  // Insert variable from dropdown (expect variable object contains id and name/label)
  const insertVariable = React.useCallback(
    (variable) => {
      if (!editorRef.current) return;
      const editor = editorRef.current;
      const id = String(variable.id);
      const label = variable.name || variable.label || `var_${id}`;
      const escapedLabel = label
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const chipHtml = `<span class="variable-chip" contenteditable="false" data-id="${id}">{${escapedLabel}}</span>&nbsp;`;
      editor.insertContent(chipHtml, { format: "raw" });
    },
    [editorRef]
  );

  // Called when Save is clicked: convert html -> tokens, extract ids
  const handleSave = async () => {
    if (!editorRef.current) return;
    const editor = editorRef.current;
    const content = editor.getContent(); // HTML
    const { text: tokenized, ids } = htmlWithChipsToTokensAndIds(content);
    setSaving(true);

    const createVariableAsync = (request) =>
      new Promise((resolve) => {
        dispatch(createVariable(request, (response) => resolve(response)));
      });

    const idMapping = {};

    const creationPromises = ids.map(async (id) => {
      const customFormField = customFormFields.find(
        (field) => String(field.id) === String(id)
      );

      if (customFormField) {
        const existingVariable = items.find(
          (item) => item.customFormField?.id === customFormField.id
        );

        if (existingVariable) {
          idMapping[String(id)] = String(existingVariable.id);
        } else {
          const newVarRequest = {
            name: customFormField.name,
            defaultValue: customFormField.name,
            type: "TEXT",
            storyboard: storyboard.id,
            customFormField: customFormField.id,
          };

          const response = await createVariableAsync(newVarRequest);
          if (response && response.id) {
            idMapping[String(id)] = String(response.id);
          }
        }
      }
    });

    await Promise.all(creationPromises);

    let updatedTokenized = tokenized;
    let updatedIds = [...ids];

    Object.keys(idMapping).forEach((customFormFieldId) => {
      const variableId = idMapping[customFormFieldId];
      const regex = new RegExp(`{{\\s*${customFormFieldId}\\s*}}`, 'g');
      updatedTokenized = updatedTokenized.replace(regex, `{{${variableId}}}`);
      const idIndex = updatedIds.indexOf(customFormFieldId);
      if (idIndex !== -1) {
        updatedIds[idIndex] = variableId;
      }
    });

    setTimeout(() => {
      dispatch(updateWidgetProperty(editorSession, uid, "desktop", {
        llmProperty: {
          prompt: updatedTokenized,
          variables: updatedIds,
        },
      }))
      
      if(_.isEmpty(updatedTokenized)){
        toast(
          <WarningToast
            heading={`Warning`}
            description={`Looks like the prompt is empty! Go ahead and add something to make it personalized.`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
      else{
        toast(
          <SuccessToast
            heading={`Success`}
            description={`The AI Personalization prompt is now in your Text Widget.`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
  
      setSaving(false);
      
      onCancel();
    }, 900);
  };


  useEffect(() => {
    const tokenized = _.get(widget, "properties.desktop.llmProperty.prompt", "") || "";
    setInitialTokenizedContent(tokenized);
  }, [llmProperty]);

  return (
    <>
    <Property.Dialog
      className={styles.textPersonalizationDialog}
      width={600}
      id={`personalization-dialog-${uid}`}
      target={`#${uid}`}
      title="AI-Personalization Message Composer"
      onCancel={onCancel}
    >
      <Property.Body>
        <div  className={styles.wrapperContainer}>
          <span className={styles.description}>
            Use the variable dropdown to add a variable to your prompt.
          </span>
          <div className={styles.editorContainer}>
          <PromptInputField
            editorRef={editorRef}
            onChange={(content, editor) => {
              setEditorHtml(content);
            }}
            value={editorHtml}
          />
      </div>
          <div className={styles.actionContainer}>
          <Dropdown show={isOpen} onToggle={()=>{setIsOpen(!isOpen)}} className={styles.variableDropDown}>
            <Dropdown.Toggle className={styles.selected}>
              <i className={cx("cleverstory-variable", styles.variableIcon)} />
              Use Variable
            </Dropdown.Toggle>
            <Dropdown.Menu
              className={styles.filterMenu}
              popperConfig={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 8], // controls vertical spacing (8px below the toggle)
                    },
                  },
                  {
                    name: "flip",
                    options: {
                      fallbackPlacements: ["top", "bottom"], // only flip vertically
                    },
                  },
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "viewport", // avoid escaping the screen
                      altAxis: false,
                      padding: 8,
                    },
                  },
                ],
                  strategy: "fixed",
                }}
            >
              <VaribaleDropdownMenu
                isOpen={isOpen}
                searchable={true}
                onCreateNew={()=>{setView("createVariable")}}
                items={items}
                fieldType={_.head(fields)?.type}
                onSelect={(item) => insertVariable(item)}
              />
            </Dropdown.Menu>
          </Dropdown>

          <Button variant="secondary" disabled={saving} className={styles.saveButton} onClick={handleSave}>
            {saving ? (
              <>
                <span className={styles.savingMessageContainer}>
                  {[...Array(3)].map((_, index) => (
                    <span key={index} className={styles.savingMessage}></span>
                  ))}
                </span>
              </>
            ) : ("Save Changes")}
          </Button>
        </div>
        </div>
      </Property.Body>
    </Property.Dialog>
    { view === "createVariable" && 
        <Property.Dialog
          className={styles.styleDialog}
          width={300}
          title="Create New Variable"
          target={`#${uid}`}
          onCancel={()=>{setView(null)}}
        >
          <Property.Body>
            <CreateVariableForm
              activeDropdown={{ name: "Text", path: "content", type: "TEXT" }}
              onSubmit={()=>{setView(null)}}
              storyboardId={storyboard.id}
            />
          </Property.Body>
      </Property.Dialog>
    }
  </>
  );
}

const PromptInputField = ({ value, onChange, editorRef }) => {

  const [isLoading, setIsLoading] = useState(false);

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData("text");
    e.preventDefault();
    
    
    const sanitized = pastedText.replace(/<[^>]*>/g, "");
    
    
    if (editorRef.current) {
    editorRef.current.execCommand("mceInsertContent", false, sanitized);
    }
  };

  return (
    <>
      {!isLoading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingMessage}></div>
        </div>
      )}
      <div className={styles.promptInputContainer}>
        {isLoading && (
          <div className={styles.customPlaceholder} id="custom-placeholder">
            <img src="/images/textAi/aiTextIcon.svg" alt="" className={styles.placeholderIcon}/>
            {!value && (
              <span id="placeholder-text">
                <span className={styles.placeholderLabel}>For Example: Based on the</span>
                <span className={styles.placeholderVariable}>{'{Weather}'}</span>
                <span className={styles.placeholderLabel}> welcome the </span>
                <span className={styles.placeholderVariable}>{'{First Name}'}</span>
              </span>
            )}
          </div>
        )}
    
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          onInit={(evt, editor) => {
            editorRef.current = editor;
            setIsLoading(true);
          }}
          className={styles.customEditorWrapper}
          value={value}
          onEditorChange={onChange}
          init={{
            menubar: false,
            statusbar: false,
            branding: false,
            toolbar: false,
            height: 80,
            max_height: 80,
            min_height: 80,
            quickbars_insert_toolbar: false,
            quickbars_selection_toolbar: false,
            browser_spellcheck: true,
            valid_elements: "*[*]",
            content_css: [],
            plugins: "",
            end_container_on_empty_block: true,
            extended_valid_elements: "span[*],var[*]",
            content_style: `
              body {
                color: #212121; 
                font-size: 12px !important; 
                padding: 13px 15px 15px 31px !important;
                margin: 0px !important;
                line-height: 18px !important; 
                box-sizing: border-box;
              } 
              .variable-chip { 
                background-color: #FFFAE8; 
                borderRadius: 4px; 
                padding: 2px 4px; 
                margin: 0 2px; 
              } 
              p { 
                margin: 0; 
              }
            `,
            setup: (editor) => {
              editor.on('keydown input', () => {
                const placeholderText = document.getElementById('placeholder-text');
                if (placeholderText && editor.getContent()) {
                  placeholderText.style.opacity = '0';
                }
              });
              
              editor.on('change keyup', () => {
                const placeholderText = document.getElementById('placeholder-text');
                if (placeholderText) {
                  if (!editor.getContent() || editor.getContent().trim() === '') {
                    placeholderText.style.opacity = '1';
                  } else {
                    placeholderText.style.opacity = '0';
                  }
                }
              });
              
              editor.on("Paste", handlePaste);
              editor.on("keydown", () => {
                editor.getBody().style.fontSize = "12px";
              });
            },
          }}
        />
      </div>
    </>
  );
};

const TextStyleEditor = ({ uid, textEditor, editorSession, setTextConfig, ...rest }) => {
  const dispatch = useDispatch();
  const bookmarkRef = React.useRef({});
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );

  const fontThemes = useSelector(state => _.get(state, `${editorSession}.present.themes`));
  const present = useSelector(state => _.get(state, `${editorSession}.present`));
  var selectedContent = textEditor.selection?.getNode();
  const fontColor = textEditor.dom?.getStyle(selectedContent, 'color', true);

  const [textStyle, setTextStyle] = useState({
    theme: null,
    fontFormat: null,
    fontFamily: null,
    fontBullet: null,
    fontBold: false,
    fontItalic: false,
    fontUnderline: false,
    fontSuperscript: false,
    fontSubscript: false,
    fontStrikeThrough: false,
    fontColor: null,
    fontBackground: null,
    action: null,
  });

  const fontAlignment = _.get(rest, `properties.desktop.alignment`);
  const lineSpacing = _.get(rest, `properties.desktop.lineSpacing`);
  const letterSpacing = _.get(rest, `properties.desktop.letterSpacing`);

  const template = _.get(rest, "template.widget") != null;

  useEffect(() => {
    if (textEditor && textEditor.quirks) {
      setupEditorEvents(textEditor);
      calculateFontStyles(textEditor);
    }

    return () => {
      cleanupEditorEvents();
    };
  }, [textEditor, textEditor.quirks]);

  useEffect(() => {
    if (textEditor && textEditor.quirks) {
      calculateFontStyles(textEditor);
    }
  }, [lineSpacing, letterSpacing]);

  const setupEditorEvents = (textEditor) => {
    textEditor.on("SelectionChange", (e) => {
      calculateFontStyles(textEditor);
    });
  };

  const cleanupEditorEvents = () => {
    bookmarkRef.current.bookmark = null;
    textEditor.off("SelectionChange");
  };

  const calculateFontStyles = (textEditor) => {
    var selectedContent = textEditor.selection?.getNode();

    var fontFamily = textEditor.queryCommandValue("FontName");

    var fontFormat = "p";
    const rootBlock = getRootBlockFromSelection(textEditor);
    const theme = getThemeFromNode(textEditor, rootBlock);

    if (rootBlock) {
      fontFormat = rootBlock.nodeName.toLowerCase();
    }

    var alignment = "JustifyLeft";
    if (textEditor.queryCommandState("JustifyLeft")) {
      alignment = "JustifyLeft";
    } else if (textEditor.queryCommandState("JustifyCenter")) {
      alignment = "JustifyCenter";
    } else if (textEditor.queryCommandState("JustifyRight")) {
      alignment = "JustifyRight";
    } else if (textEditor.queryCommandState("JustifyFull")) {
      alignment = "JustifyFull";
    }

    var bullet = null;
    if (textEditor.queryCommandState("InsertOrderedList")) {
      bullet = "ol";
    } else if (textEditor.queryCommandState("InsertUnorderedList")) {
      bullet = "ul";
    }

    var size = textEditor.queryCommandValue("FontSize");
    size = parseInt(size);

    var bold = textEditor.queryCommandState("Bold");
    const italic = textEditor.queryCommandState("Italic");
    const underline = textEditor.queryCommandState("Underline");
    const subscript = textEditor.queryCommandState("Subscript");
    const superscript = textEditor.queryCommandState("Superscript");
    const strikethrough = textEditor.queryCommandState("Strikethrough");

    const fontColor = textEditor.dom?.getStyle(selectedContent, "color", true);
    const fontBackground = extractBackgroundColorFromElement(selectedContent);

    const linkElement = textEditor.dom?.getParent(
      selectedContent,
      "a[data-action]"
    );
    const actionId = linkElement ? linkElement.dataset.action : null;

    setTextStyle((state) => {
      return {
        ...state,
        theme: theme,
        fontFormat: fontFormat,
        fontFamily: fontFamily,
        fontBullet: bullet,
        fontSize: size,
        lineSpacing: lineSpacing,
        letterSpacing: letterSpacing,
        fontBold: bold,
        fontItalic: italic,
        fontUnderline: underline,
        fontStrikeThrough: strikethrough,
        fontSuperscript: superscript,
        fontSubscript: subscript,
        fontColor: fontColor ? fontColor : state.fontColor,
        fontBackground: fontBackground,
        action: actionId,
      };
    });
  };

  const handleThemeChange = (value) => {
    var format = _.get(value, "value", "p");
    const themeStyle = fontThemes.find((data) => data.type === format);
    applyThemeFormat(themeStyle);
  };

  const applyThemeFormat = (theme) => {
    textEditor.undoManager.transact(() => {
      const selectAll = isAllContentSelected(textEditor);
      if (selectAll) {
        textEditor.execCommand("SelectAll");
      } else {
        const rootBlock = getRootBlockFromSelection(textEditor);
        if (rootBlock) {
          textEditor.selection.select(rootBlock);
        }
      }

      const format = _.get(theme, "type");
      const bold = _.get(theme, "font.style.bold");
      const italic = _.get(theme, "font.style.italic");

      const fontSize = _.get(theme, "font.fontSize");
      const fontFamily = _.get(theme, "font.fontFamily");
      const color = _.get(theme, "font.color");

      textEditor.execCommand("RemoveFormat");

      if (textStyle.fontFormat != format) {
        textEditor.execCommand("FormatBlock", false, format);
      }

      textEditor.execCommand("FontName", false, fontFamily);

      if (bold) {
        textEditor.execCommand("Bold", false);
      }

      if (italic) {
        textEditor.execCommand("Italic", false);
      }

      handleSpacingChange({
        lineSpacing: _.get(theme, "font.lineSpacing"),
        letterSpacing: _.get(theme, "font.letterSpacing"),
      });

      if (color) {
        textEditor.execCommand(
          "mceApplyTextcolor",
          "forecolor",
          colorToString(color)
        );
      }

      handleSizeChange(fontSize);
    });
  };

  const handleFontFamily = (value) => {
    var family = _.get(value, "value");
    if (!family) {
      return;
    }

    textEditor.execCommand("FontName", false, family);
    calculateFontStyles(textEditor);
  };

  const handleAlignmentChange = (value) => {
    var alignment = _.get(value, "value");
    if (!alignment) {
      return;
    }

    dispatch(
      updateWidgetProperty(editorSession, uid, "desktop", {
        alignment: alignment,
      })
    );
  };

  const handleBulletsChange = (value) => {
    var bullet = _.get(value, "value");

    if (bullet === 'ol') {
      textEditor.execCommand('InsertOrderedList', false, {});
    } else if (bullet === 'ul') {
      textEditor.execCommand('InsertUnorderedList', false, {});
      textEditor.fire('change', {});
    } else if (bullet === 'none') {
      try {
        const activeEl = textEditor.targetElm;
        if (textEditor.queryCommandState('InsertOrderedList')) {
          textEditor.execCommand('InsertOrderedList', false, {});
        }
        if (textEditor.queryCommandState('InsertUnorderedList')) {
          textEditor.execCommand('InsertUnorderedList', false, {});
        }
        textEditor.execCommand('RemoveList');
        if(activeEl){
          const span = activeEl.querySelector('span');
          const parentP = span?.closest('p');
          if (span && parentP) {
            const computedFontSize = window.getComputedStyle(span).fontSize;
            parentP.style.fontSize = computedFontSize;
          }
        }
      } catch {
        console.error("Error removing list");
      }
    }
  };

  const handleSizeChange = (value) => {
    if (
      textEditor.bodyElement.textContent ===
      textEditor.selection?.getNode().textContent
    ) {
      //cleanFontSizeFromElement(textEditor, textEditor.bodyElement);

      textEditor.formatter.apply("fontsize_parent", { value: `${value}px` });
      textEditor.fire("change", {});

      actionGroup.start();

      dispatch(
        updateWidgetProperty(editorSession, uid, "desktop", {
          fontSize: value,
        })
      );

      const mobileFontSize = calculateFontSizeForMobile(value);
      dispatch(
        updateWidgetProperty(editorSession, uid, "mobile", {
          fontSize: mobileFontSize,
        })
      );

      actionGroup.end();
    }

    textEditor.execCommand("fontsize", false, `${value}px`, {
      skip_focus: true,
    });

    calculateFontStyles(textEditor);
  };

  const handleSpacingChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, uid, "desktop", {
        ...value,
      })
    );
  };

  const handleBoldToggle = () => {
    textEditor.execCommand("Bold", false);
    calculateFontStyles(textEditor);
  };

  const handleItalicsToggle = () => {
    textEditor.execCommand("Italic", false);
    calculateFontStyles(textEditor);
  };

  const handleUnderlineToggle = () => {
    textEditor.execCommand("Underline", false);
    calculateFontStyles(textEditor);
  };

  const handleStrikeThroughToggle = () => {
    textEditor.execCommand("StrikeThrough", false);
    calculateFontStyles(textEditor);
  };

  const handleSuperscriptToggle = () => {
    textEditor.execCommand("Superscript", false);
    calculateFontStyles(textEditor);
  };

  const handleSubscriptToggle = () => {
    textEditor.execCommand("Subscript", false);
    calculateFontStyles(textEditor);
  };

  const handleColorChange = (colorRgb) => {
    if (colorRgb) {
      var colorData = colorRgb.color || colorRgb;
      var color = `rgba(${colorData.r}, ${colorData.g}, ${colorData.b}, ${colorData.a})`;
      textEditor.execCommand("mceApplyTextcolor", "forecolor", color);

      calculateFontStyles(textEditor);
    }
  };

  const handleBackgroundChange = (colorRgb) => {
    if (colorRgb) {
      var colorData = colorRgb.color || colorRgb;
      var color = `rgba(${colorData.r}, ${colorData.g}, ${colorData.b}, ${colorData.a})`;
      textEditor.execCommand("mceApplyTextcolor", "hilitecolor", color, {
        skip_focus: true,
      });
    } else {
      textEditor.execCommand("mceRemoveTextcolor", "hilitecolor");
    }

    calculateFontStyles(textEditor);
  };

  // Resetting text editor
  const handleReset = () => {
    const widgets = _.get(present, "widgets");
    const themes = _.get(present, "themes");
    var selectedWidget = widgets.find((item) => item.uid === uid);
    const editorId = selectedWidget.uid + "_editor";
    const editorElement = document.getElementById(editorId);

    if (textEditor) {
      textEditor.formatter.remove("strikethrough");
      textEditor.formatter.remove("superscript");
      textEditor.formatter.remove("subscript");
    }

    if (editorElement) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = editorElement.innerHTML;

      // function removeAttributes(element) {
      //   if (element && element.nodeType === Node.ELEMENT_NODE) {
      //     // ✅ Skip removing attributes from variable-wrapper spans
      //     if (
      //       element.classList.contains("variable-wrapper") ||
      //       element.classList.contains("variable-placeholder")
      //     ) {
      //       // But still process children to ensure nested spans are cleaned
      //       Array.from(element.children).forEach(removeAttributes);
      //       return;
      //     }

      //     const attributes = element.attributes;
      //     for (let i = attributes.length - 1; i >= 0; i--) {
      //       element.removeAttribute(attributes[i].name);
      //     }

      //     Array.from(element.children).forEach(removeAttributes);
      //   }
      // }
      function removeAttributes(element) {
        if (element && element.nodeType === Node.ELEMENT_NODE) {
          // ✅ Skip removing anything from variable spans
          if (
            element.classList.contains("mce-variable") &&
            element.classList.contains("mceNonEditable")
          ) {
            // Still clean children in case any nested formatting exists
            Array.from(element.children).forEach(removeAttributes);
            return;
          }

          // ✅ Remove only the 'style' attribute from other elements
          if (element.hasAttribute("style")) {
            element.removeAttribute("style");
          }

          Array.from(element.children).forEach(removeAttributes);
        }
      }

      removeAttributes(tempDiv);

      const editedHtml = tempDiv.innerHTML;
      const htmlArray = editedHtml
        .split(/(?<=<\/[^>]+>)/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      const resultArray = [];
      let openTag = "";

      for (const item of htmlArray) {
        if (item.startsWith("</")) {
          openTag += item;
        } else {
          if (openTag !== "") {
            resultArray.push(openTag);
          }
          openTag = item;
        }
      }

      if (openTag !== "") {
        resultArray.push(openTag);
      }

      const outputString = resultArray.join("\n");
      const result = parseHtmlString(outputString);

      function parseHtmlString(outputString) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(outputString, "text/html");
        const elements = Array.from(doc.body.children);

        const data = elements.map((element) => {
          const type = element.tagName.toLowerCase();
          const innerHtml = element.innerHTML;
          return { type, innerHtml };
        });

        return data;
      }

      const createThemeContent = (data, themes) => {
        let textTheme = themes.find((theme) => theme.type === data.type);
        const fontStyles = _.get(textTheme, "font", {});
        const textStyles = _.get(textTheme, "font.style", {});
        const lineSpacing = _.get(fontStyles, "lineSpacing") || 1.2;
        const letterSpacing = _.get(fontStyles, "letterSpacing") || 0;
        const fontSize = _.get(fontStyles, "fontSize") || 16;

        if (selectedWidget) {
          const request = {
            ...selectedWidget.properties.desktop,
            lineSpacing: lineSpacing,
            letterSpacing: letterSpacing,
            fontSize: fontSize,
          };
          dispatch(
            updateWidgetProperty(
              editorSession,
              selectedWidget.uid,
              "desktop",
              request
            )
          );
        }

        let themeContent = "";

        switch (true) {
          case textStyles.bold && textStyles.italic:
            themeContent = `
              <${data.type}>
                <span style="font-weight: bold; font-style: italic;">${data.innerHtml}</span>
              </${data.type}>
            `;
            break;

          case textStyles.bold:
            themeContent = `
              <${data.type}>
                <span style="font-weight: bold;">${data.innerHtml}</span>
              </${data.type}>
            `;
            break;

          case textStyles.italic:
            themeContent = `
              <${data.type}>
                <span style="font-style: italic;">${data.innerHtml}</span>
              </${data.type}>
            `
          break;

          default:
            themeContent = `
              <${data.type} style="font-size:${fontStyles.fontSize}px">
                <span style="font-size:${fontStyles.fontSize}px">${data.innerHtml}</span>
              </${data.type}>
            `
          break;
        }
        return themeContent;
      };

      const resetThemeContent = result.map((data) => {
        var updateContent = createThemeContent(data, themes);
        return updateContent;
      });

      const finalContent =
        resetThemeContent.length > 1
          ? resetThemeContent.slice(0, -1).join("\n") +
            resetThemeContent[resetThemeContent.length - 1]
          : resetThemeContent[0];

      setTextConfig((p) => ({
        ...p,
        editor,
        id: selectedWidget.uid,
        content: finalContent,
      }));
    }
  };

  const handleLinkChange = (value) => {
    const actions = rest.actions || [];
    const updated = [...actions, value];
    dispatch(updateWidgetActions(editorSession, uid, updated));
  };

  const handleUpdateThemes = (theme) => {
    const font = _.get(theme, "font");
    if (font) {
      dispatch(updateEditorThemes(editorSession, textStyle.fontFormat, font));
    }
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, rest.uid, false, null));
  };

  return (
    <>
      <Property.Dialog
        className={styles.fontDialog}
        width={236}
        target={`#${uid}`}
        title="Text Settings"
        onCancel={closePopup}
      >
        <Property.Body>
          <Font.Theme
            value={textStyle.theme}
            options={fontThemes}
            onChange={handleThemeChange}
            onSave={handleUpdateThemes}
            onReset={handleReset}
          />
          <Font.Family
            value={textStyle.fontFamily}
            onChange={handleFontFamily}
          />
          <Font.Size
            min={6}
            max={300}
            value={textStyle.fontSize}
            onChange={handleSizeChange}
          />
          <Property>
            <div className={styles.fontActions}>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: textStyle.fontBold,
                })}
                tooltip="Bold"
                onClick={handleBoldToggle}
              >
                <i className={"cleverstory-bold"} />
              </Property.Action>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: textStyle.fontItalic,
                })}
                tooltip="Italic"
                onClick={handleItalicsToggle}
              >
                <i className={"cleverstory-italics"} />
              </Property.Action>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: textStyle.fontUnderline,
                })}
                tooltip="Underline"
                onClick={handleUnderlineToggle}
              >
                <i className={"cleverstory-underline"} />
              </Property.Action>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: textStyle.fontStrikeThrough,
                })}
                tooltip="Strikethrough"
                onClick={handleStrikeThroughToggle}
              >
                <i className={"cleverstory-strike-through"} />
              </Property.Action>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: textStyle.fontSuperscript,
                })}
                tooltip="Superscript"
                onClick={handleSuperscriptToggle}
              >
                <i className={"cleverstory-superscript"} />
              </Property.Action>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: textStyle.fontSubscript,
                })}
                tooltip="Subscript"
                onClick={handleSubscriptToggle}
              >
                <i className={"cleverstory-subscript"} />
              </Property.Action>
              {!template && (
                <TextLinkAction
                  id={uid}
                  editorSession={editorSession}
                  textEditor={textEditor}
                  onChange={handleLinkChange}
                />
              )}
              <Font.Color
                value={textStyle.fontColor}
                onChange={handleColorChange}
              />
              <Font.Background
                value={textStyle.fontBackground}
                onChange={handleBackgroundChange}
              />
            </div>
          </Property>
          <Property name="Alignment">
            <div className={styles.alignmentActions}>
              <Font.Alignment
                value={fontAlignment}
                onChange={handleAlignmentChange}
              />
            </div>
          </Property>
          <Property name="List">
            <div className={styles.alignmentActions}>
              <Font.Bullets
                value={textStyle.fontBullet}
                onChange={handleBulletsChange}
              />
            </div>
          </Property>
          <Font.Spacing
            value={{
              lineSpacing: lineSpacing,
              letterSpacing: letterSpacing,
            }}
            onChange={handleSpacingChange}
          />
        </Property.Body>
      </Property.Dialog>
    </>
  );
};

const MultiTextStyleEditor = ({ editorSession, selectedWidgets }) => {
  const dispatch = useDispatch();    
  const fontThemes = useSelector(state => _.get(state, `${editorSession}.present.themes`));

  const DEFAULT_TEXT_STYLE = {
    theme: null,
    fontFormat: null,
    fontFamily: null,
    fontBullet: null,
    fontBold: false,
    fontItalic: false,
    fontUnderline: false,
    fontSuperscript: false,
    fontSubscript: false,
    fontStrikeThrough: false,
    fontColor: null,
    fontBackground: null,
    fontSize: null,
    alignment: null,
    lineSpacing: null,
    letterSpacing: null,
  };
  
  const [batchTextStyle, setBatchTextStyle] = useState(DEFAULT_TEXT_STYLE);

  useEffect(() => {
    if (selectedWidgets?.length === 0) {
      setBatchTextStyle(DEFAULT_TEXT_STYLE);
    }
  }, [selectedWidgets]);

  const mergeStyles = (currentStyle, newProperty, newValue) => {
    const styles = {};
    
    if (currentStyle) {
      currentStyle.split(';').forEach(rule => {
        const [property, value] = rule.split(':').map(s => s.trim());
        if (property && value) {
          styles[property] = value;
        }
      });
    }
    
    if (newValue !== null && newValue !== undefined) {
      styles[newProperty] = newValue;
    } else {
      delete styles[newProperty];
    }
    
    return Object.entries(styles)
      .filter(([key, value]) => key && value)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');
  };

  const extractTextContent = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const hasStyleProperty = (content, property, value = null) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const textElements = tempDiv.querySelectorAll('span, p, h1, h2, h3, h4, h5, h6, li');
    
    return Array.from(textElements).some(element => {
      const style = element.getAttribute('style') || '';
      const computedStyle = window.getComputedStyle ? window.getComputedStyle(element) : null;
      
      if (value) {
        return style.includes(`${property}: ${value}`) || 
               style.includes(`${property}:${value}`) ||
               (computedStyle && computedStyle.getPropertyValue(property) === value);
      }
      return style.includes(`${property}:`) || 
             (computedStyle && computedStyle.getPropertyValue(property) !== '');
    });
  }; 

  const hasElementType = (content, tagName) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    return tempDiv.querySelectorAll(tagName).length > 0;
  };

  const handleBatchThemeChange = (value) => {
    const format = _.get(value, "value", "p");
    const themeStyle = fontThemes.find((data) => data.type === format);
    
    if (!themeStyle) return;

    setBatchTextStyle(prev => ({
      ...prev,
      theme: value,
      fontFormat: format
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = applyThemeToContent(widget.content, themeStyle);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
      
      const font = _.get(themeStyle, "font");
      if (font) {
        handleBatchSizeChange({
          lineSpacing: font?.lineSpacing,
          letterSpacing: font?.letterSpacing
        });
        
        handleBatchSizeChange(font?.fontSize);
      }
    });
    
    actionGroup.end();
  };

  const applyThemeToContent = (content, theme) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const format = _.get(theme, "type");
    const bold = _.get(theme, "font.style.bold");
    const italic = _.get(theme, "font.style.italic");
    const fontSize = _.get(theme, "font.fontSize");
    const fontFamily = _.get(theme, "font.fontFamily");
    const color = _.get(theme, "font.color");
    
    const elements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
    elements.forEach(element => {
      const textContent = extractTextContent(element.innerHTML);
      
      const newElement = document.createElement(format);
      
      if (fontSize) {
        newElement.style.fontSize = `${fontSize}px`;
      }
      
      let innerContent = textContent;
      
      const spanStyles = [];
      if (fontSize) spanStyles.push(`font-size: ${fontSize}px`);
      if (bold) spanStyles.push(`font-weight: bold`);
      if (italic) spanStyles.push(`font-style: italic`);
      if (color) spanStyles.push(`color: ${colorToString(color)}`);
      
      if (spanStyles.length > 0) {
        innerContent = `<span style="${spanStyles.join('; ')};">${innerContent}</span>`;
      }
      
      newElement.innerHTML = innerContent;
      element.replaceWith(newElement);
    });
    
    return tempDiv.innerHTML;
  };

  const handleBatchFontFamily = (value) => {
    const family = _.get(value, "value");
    if (!family) return;

    setBatchTextStyle(prev => ({
      ...prev,
      fontFamily: family
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = applyFontFamilyToContent(widget.content, family);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const applyFontFamilyToContent = (content, fontFamily) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const spans = tempDiv.querySelectorAll('span');
    
    if (spans.length > 0) {
      spans.forEach(element => {
        const currentStyle = element.getAttribute('style') || '';
        const newStyle = mergeStyles(currentStyle, 'font-family', fontFamily);
        element.setAttribute('style', newStyle);
      });
    } else {
      const textElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
      textElements.forEach(element => {
        const textContent = element.textContent || element.innerText;
        if (textContent.trim()) {
          const span = document.createElement('span');
          span.style.fontFamily = fontFamily;
          span.textContent = textContent;
          element.innerHTML = '';
          element.appendChild(span);
        }
      });
    }
    
    return tempDiv.innerHTML;
  };

  const handleBatchSizeChange = (value) => {

    setBatchTextStyle(prev => ({
      ...prev,
      fontSize: value
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      dispatch(updateWidgetProperty(editorSession, widget.uid, "desktop", {
        fontSize: value,
      }));
      
      const mobileFontSize = calculateFontSizeForMobile(value);
      dispatch(updateWidgetProperty(editorSession, widget.uid, "mobile", {
        fontSize: mobileFontSize,
      }));
      
      const updatedContent = applyFontSizeToContent(widget.content, value);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const applyFontSizeToContent = (content, fontSize) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const rootElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
    rootElements.forEach(element => {
      element.style.fontSize = `${fontSize}px`;
    });
    
    const spans = tempDiv.querySelectorAll('span');
    spans.forEach(element => {
      const currentStyle = element.getAttribute('style') || '';
      const newStyle = mergeStyles(currentStyle, 'font-size', `${fontSize}px`);
      element.setAttribute('style', newStyle);
    });
    
    if (spans.length === 0) {
      rootElements.forEach(element => {
        const textContent = element.textContent || element.innerText;
        if (textContent.trim()) {
          const span = document.createElement('span');
          span.style.fontSize = `${fontSize}px`;
          span.textContent = textContent;
          element.innerHTML = '';
          element.appendChild(span);
        }
      });
    }
    
    return tempDiv.innerHTML;
  };

  const handleBatchBoldToggle = () => {
    const allHaveBold = selectedWidgets.every(widget => 
      hasStyleProperty(widget.content, 'font-weight', 'bold')
    );
    
    const newBoldState = !allHaveBold;
    
    setBatchTextStyle(prev => ({
      ...prev,
      fontBold: newBoldState
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = toggleBoldInContent(widget.content, newBoldState);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const toggleBoldInContent = (content, shouldApply) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const spans = tempDiv.querySelectorAll('span');
    
    if (spans.length > 0) {
      spans.forEach(element => {
        const currentStyle = element.getAttribute('style') || '';
        const newStyle = mergeStyles(currentStyle, 'font-weight', shouldApply ? 'bold' : null);
        element.setAttribute('style', newStyle);
      });
    } else {
      const textElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
      textElements.forEach(element => {
        const textContent = element.textContent || element.innerText;
        if (textContent.trim()) {
          const span = document.createElement('span');
          if (shouldApply) {
            span.style.fontWeight = 'bold';
          }
          span.textContent = textContent;
          element.innerHTML = '';
          element.appendChild(span);
        }
      });
    }
    
    return tempDiv.innerHTML;
  };

  const handleBatchItalicToggle = () => {
    const allHaveItalic = selectedWidgets.every(widget => 
      hasStyleProperty(widget.content, 'font-style', 'italic')
    );
    
    const newItalicState = !allHaveItalic;
    
    setBatchTextStyle(prev => ({
      ...prev,
      fontItalic: newItalicState
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = toggleItalicInContent(widget.content, newItalicState);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const toggleItalicInContent = (content, shouldApply) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const spans = tempDiv.querySelectorAll('span');
    
    if (spans.length > 0) {
      spans.forEach(element => {
        const currentStyle = element.getAttribute('style') || '';
        const newStyle = mergeStyles(currentStyle, 'font-style', shouldApply ? 'italic' : null);
        element.setAttribute('style', newStyle);
      });
    } else {
      const textElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
      textElements.forEach(element => {
        const textContent = element.textContent || element.innerText;
        if (textContent.trim()) {
          const span = document.createElement('span');
          if (shouldApply) {
            span.style.fontStyle = 'italic';
          }
          span.textContent = textContent;
          element.innerHTML = '';
          element.appendChild(span);
        }
      });
    }
    
    return tempDiv.innerHTML;
  };

  const handleBatchUnderlineToggle = () => {
    const allHaveUnderline = selectedWidgets.every(widget => 
      hasStyleProperty(widget.content, 'text-decoration', 'underline')
    );
    
    const newUnderlineState = !allHaveUnderline;
    
    setBatchTextStyle(prev => ({
      ...prev,
      fontUnderline: newUnderlineState
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = applyUnderlineToContent(widget.content, newUnderlineState);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const applyUnderlineToContent = (content, shouldApply) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const spans = tempDiv.querySelectorAll('span');
    const listItems = tempDiv.querySelectorAll('li');
    
    if (spans.length > 0) {
      spans.forEach(element => {
        const currentStyle = element.getAttribute('style') || '';
        const newStyle = mergeStyles(currentStyle, 'text-decoration', shouldApply ? 'underline' : null);
        element.setAttribute('style', newStyle);
      });
    } else {
      const textElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
      textElements.forEach(element => {
        const textContent = element.textContent || element.innerText;
        if (textContent.trim()) {
          const span = document.createElement('span');
          if (shouldApply) {
            span.style.textDecoration = 'underline';
          }
          span.textContent = textContent;
          element.innerHTML = '';
          element.appendChild(span);
        }
      });
    }
    
    listItems.forEach(element => {
      const currentStyle = element.getAttribute('style') || '';
      const newStyle = mergeStyles(currentStyle, 'text-decoration', shouldApply ? 'underline' : null);
      element.setAttribute('style', newStyle);
    });
    
    return tempDiv.innerHTML;
  };

  const handleBatchStrikeThroughToggle = () => {
    const allHaveStrikethrough = selectedWidgets.every(widget => 
      hasElementType(widget.content, 's')
    );
    
    const newStrikeThroughState = !allHaveStrikethrough;
    
    setBatchTextStyle(prev => ({
      ...prev,
      fontStrikeThrough: newStrikeThroughState
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = toggleStrikeThroughInContent(widget.content, newStrikeThroughState);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const toggleStrikeThroughInContent = (content, shouldApply) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    if (shouldApply) {
      const hasStrikethrough = hasElementType(content, 's');
      if (!hasStrikethrough) {
        const rootElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li');
        rootElements.forEach(element => {
          const spans = element.querySelectorAll('span');
          const innermostSpan = spans[spans.length - 1]; 
          
          if (innermostSpan) {
            const sTag = document.createElement('s');
            sTag.innerHTML = innermostSpan.innerHTML;
            innermostSpan.innerHTML = '';
            innermostSpan.appendChild(sTag);
          } else {
            const sTag = document.createElement('s');
            sTag.innerHTML = element.innerHTML;
            element.innerHTML = '';
            element.appendChild(sTag);
          }
        });
      }
    } else {
      const sTags = tempDiv.querySelectorAll('s');
      sTags.forEach(s => {
        s.outerHTML = s.innerHTML;
      });
    }
    
    return tempDiv.innerHTML;
  };

  const handleBatchColorChange = (colorRgb) => {
    if (!colorRgb) return;
    
    const colorData = colorRgb.color || colorRgb;
    const color = `rgb(${colorData.r}, ${colorData.g}, ${colorData.b})`;

    setBatchTextStyle(prev => ({
      ...prev,
      fontColor: colorRgb
    }));
    
    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = applyColorToContent(widget.content, color);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const applyColorToContent = (content, color) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const spans = tempDiv.querySelectorAll('span');
    const listItems = tempDiv.querySelectorAll('li');
    
    if (spans.length > 0) {
      spans.forEach(element => {
        const currentStyle = element.getAttribute('style') || '';
        const newStyle = mergeStyles(currentStyle, 'color', color);
        element.setAttribute('style', newStyle);
      });
    } else {
      const textElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
      textElements.forEach(element => {
        const textContent = element.textContent || element.innerText;
        if (textContent.trim()) {
          const span = document.createElement('span');
          span.style.color = color;
          span.textContent = textContent;
          element.innerHTML = '';
          element.appendChild(span);
        }
      });
    }
    
    listItems.forEach(element => {
      const currentStyle = element.getAttribute('style') || '';
      const newStyle = mergeStyles(currentStyle, 'color', color);
      element.setAttribute('style', newStyle);
    });
    
    return tempDiv.innerHTML;
  };

  const handleBatchBackgroundChange = (colorRgb) => {
    const colorData = colorRgb?.color || colorRgb;

    setBatchTextStyle(prev => ({
      ...prev,
      fontBackground: colorData ? colorRgb : null
    }));
    
    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = colorData 
        ? applyBackgroundColorToContent(widget.content, `rgb(${colorData.r}, ${colorData.g}, ${colorData.b})`)
        : removeBackgroundColorFromContent(widget.content);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const applyBackgroundColorToContent = (content, backgroundColor) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const spans = tempDiv.querySelectorAll('span');
    
    if (spans.length > 0) {
      spans.forEach(element => {
        const currentStyle = element.getAttribute('style') || '';
        const newStyle = mergeStyles(currentStyle, 'background-color', backgroundColor);
        element.setAttribute('style', newStyle);
      });
    } else {
      const textElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
      textElements.forEach(element => {
        const textContent = element.textContent || element.innerText;
        if (textContent.trim()) {
          const span = document.createElement('span');
          span.style.backgroundColor = backgroundColor;
          span.textContent = textContent;
          element.innerHTML = '';
          element.appendChild(span);
        }
      });
    }
    
    return tempDiv.innerHTML;
  };  

  const removeBackgroundColorFromContent = (content) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const spans = tempDiv.querySelectorAll('span');
    spans.forEach(element => {
      const currentStyle = element.getAttribute('style') || '';
      const newStyle = mergeStyles(currentStyle, 'background-color', null);
      element.setAttribute('style', newStyle);
    });
    
    return tempDiv.innerHTML;
  };

  const handleBatchAlignmentChange = (value) => {
    const alignment = _.get(value, "value");
    if (!alignment) return;
  
    setBatchTextStyle(prev => ({
      ...prev,
      alignment: value
    }));
  
    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      dispatch(updateWidgetProperty(editorSession, widget.uid, "desktop", {
        alignment: alignment,
      }));
    });
    
    actionGroup.end();
  };

  const handleBatchSpacingChange = (value) => {
    setBatchTextStyle(prev => ({
      ...prev,
      lineSpacing: value.lineSpacing !== undefined ? value.lineSpacing : prev.lineSpacing,
      letterSpacing: value.letterSpacing !== undefined ? value.letterSpacing : prev.letterSpacing
    }));
  
    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      dispatch(updateWidgetProperty(editorSession, widget.uid, "desktop", {
        ...value,
      }));
    });
    
    actionGroup.end();
  };

  const handleBatchBulletsChange = (value) => {
    const bullet = _.get(value, "value");
    
    const allHaveBulletType = selectedWidgets.every(widget => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = widget.content;
      
      if (bullet === 'ol') {
        return tempDiv.querySelectorAll('ol').length > 0;
      } else if (bullet === 'ul') {
        return tempDiv.querySelectorAll('ul').length > 0;
      }
      return false;
    });
    
    const newBullet = allHaveBulletType ? null : bullet;
    
    setBatchTextStyle(prev => ({
      ...prev,
      fontBullet: newBullet ? value : null
    }));
    
    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      const updatedContent = applyBulletToContent(widget.content, newBullet);
      dispatch(updateWidgetContent(editorSession, widget.uid, updatedContent));
    });
    
    actionGroup.end();
  };

  const applyBulletToContent = (content, bulletType) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const existingLists = tempDiv.querySelectorAll('ol, ul');
    existingLists.forEach(list => {
      const items = list.querySelectorAll('li');
      const fragment = document.createDocumentFragment();
      
      items.forEach(item => {
        const p = document.createElement('p');
        p.innerHTML = item.innerHTML;
        if (item.getAttribute('style')) {
          p.setAttribute('style', item.getAttribute('style'));
        }
        fragment.appendChild(p);
      });
      
      list.parentNode.replaceChild(fragment, list);
    });
    
    if (!bulletType || bulletType === 'none') {
      return tempDiv.innerHTML;
    }
    
    const textElements = Array.from(tempDiv.children).filter(el => 
      ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV'].includes(el.tagName)
    );
    
    if (textElements.length > 0) {
      const listElement = document.createElement(bulletType === 'ol' ? 'ol' : 'ul');
      
      textElements.forEach(element => {
        const li = document.createElement('li');
        li.innerHTML = element.innerHTML;
        
        if (element.getAttribute('style')) {
          const elementStyle = element.getAttribute('style');
          const fontSizeMatch = elementStyle.match(/font-size:\s*([^;]+)/);
          if (fontSizeMatch) {
            li.setAttribute('style', `font-size: ${fontSizeMatch[1]}`);
          }
        }
        
        listElement.appendChild(li);
        element.remove();
      });
      
      tempDiv.appendChild(listElement);
    }
    
    return tempDiv.innerHTML;
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, selectedWidgets[0]?.uid, false, null, false));
  };

  return (
    <Property.Dialog
      className={styles.fontDialog}
      target={`#${selectedWidgets[0]?.uid}`}
      width={236}
      title="Text Settings"
      onCancel={closePopup}
    >
      <Property.Body>        
        <Font.Theme
          value={batchTextStyle.theme}
          options={fontThemes}
          onChange={handleBatchThemeChange}
          isBatch={true}
        />
        
        <Font.Family
          value={batchTextStyle.fontFamily}
          onChange={handleBatchFontFamily}
        />
        
        <Font.Size
          min={6}
          max={300}
          value={batchTextStyle.fontSize}
          onChange={handleBatchSizeChange}
        />
        
        <Property>
          <div className={styles.fontActions}>
            <Property.Action
              className={cx(styles.iconButton, {
                [styles.active]: batchTextStyle.fontBold,
              })}
              tooltip="Bold"
              onClick={handleBatchBoldToggle}
            >
              <i className={"cleverstory-bold"} />
            </Property.Action>
            <Property.Action
              className={cx(styles.iconButton, {
                [styles.active]: batchTextStyle.fontItalic,
              })}
              tooltip="Italic"
              onClick={handleBatchItalicToggle}
            >
              <i className={"cleverstory-italics"} />
            </Property.Action>
            <Property.Action
              className={cx(styles.iconButton, {
                [styles.active]: batchTextStyle.fontUnderline,
              })}
              tooltip="Underline"
              onClick={handleBatchUnderlineToggle}
            >
              <i className={"cleverstory-underline"} />
            </Property.Action>
            <Property.Action
              className={cx(styles.iconButton, {
                [styles.active]: batchTextStyle.fontStrikeThrough,
              })}
              tooltip="Strikethrough"
              onClick={handleBatchStrikeThroughToggle}
            >
              <i className={"cleverstory-strike-through"} />
            </Property.Action>
            
            <Font.Color
              value={batchTextStyle.fontColor}
              onChange={handleBatchColorChange}
            />
            <Font.Background
              value={batchTextStyle.fontBackground}
              onChange={handleBatchBackgroundChange}
            />
          </div>
        </Property>
        
        <Property name="Alignment">
          <div className={styles.alignmentActions}>
            <Font.Alignment
              value={batchTextStyle.alignment}
              onChange={handleBatchAlignmentChange}
            />
          </div>
        </Property>

        <Property name="List">
          <div className={styles.alignmentActions}>
            <Font.Bullets
              value={batchTextStyle.fontBullet?.value}
              onChange={handleBatchBulletsChange}
            />
          </div>
        </Property>
        
        <Font.Spacing
          value={{
            lineSpacing: batchTextStyle.lineSpacing,
            letterSpacing: batchTextStyle.letterSpacing,
          }}
          onChange={handleBatchSpacingChange}
        />
      </Property.Body>
    </Property.Dialog>
  );
};

const MultiMobileTextStyleEditor = ({ editorSession, selectedWidgets }) => {
  const dispatch = useDispatch();

  const DEFAULT_MOBILE_TEXT_STYLE = {
    fontSize: null,
    alignment: null,
    lineSpacing: null,
    letterSpacing: null,
  };

  const [batchMobileTextStyle, setBatchMobileTextStyle] = useState(DEFAULT_MOBILE_TEXT_STYLE);

  useEffect(() => {
    if (selectedWidgets?.length === 0) {
      setBatchMobileTextStyle(DEFAULT_MOBILE_TEXT_STYLE);
    }
  }, [selectedWidgets]);

  const handleBatchSizeChange = (value) => {
    setBatchMobileTextStyle(prev => ({
      ...prev,
      fontSize: value
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      dispatch(updateWidgetProperty(editorSession, widget.uid, "mobile", {
        fontSize: value,
      }));
    });
    
    actionGroup.end();
  };

  const handleBatchAlignmentChange = (value) => {
    setBatchMobileTextStyle(prev => ({
      ...prev,
      alignment: value
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      dispatch(updateWidgetProperty(editorSession, widget.uid, "mobile", {
        alignment: value,
      }));
    });
    
    actionGroup.end();
  };

  const handleBatchSpacingChange = (value) => {
    setBatchMobileTextStyle(prev => ({
      ...prev,
      lineSpacing: value.lineSpacing !== undefined ? value.lineSpacing : prev.lineSpacing,
      letterSpacing: value.letterSpacing !== undefined ? value.letterSpacing : prev.letterSpacing
    }));

    actionGroup.start();
    
    selectedWidgets.forEach(widget => {
      dispatch(updateWidgetProperty(editorSession, widget.uid, "mobile", {
        ...value,
      }));
    });
    
    actionGroup.end();
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, selectedWidgets[0]?.uid, false, null, false));
  };

  return (
    <Property.Dialog
      className={cx(styles.fontDialog, styles.mobile)}
      target={`#${selectedWidgets[0]?.uid}`}
      width={222}
      title="Text Settings"
      onCancel={closePopup}
    >
      <Property.Body>
        <Font.Size
          min={6}
          max={150}
          value={batchMobileTextStyle.fontSize}
          onChange={handleBatchSizeChange}
          placeholder="Mixed sizes..."
        />
        
        <Property name="Alignment">
          <div className={styles.alignmentActions}>
            <Tooltip placement="top" content="Left Align">
              <div>
                <Property.Toggle
                  icon={faAlignLeft}
                  active={batchMobileTextStyle.alignment === "left"}
                  onChange={() => handleBatchAlignmentChange("left")}
                />
              </div>
            </Tooltip>
            <Tooltip placement="top" content="Center Align">
              <div>
                <Property.Toggle
                  icon={faAlignCenter}
                  active={batchMobileTextStyle.alignment === "center"}
                  onChange={() => handleBatchAlignmentChange("center")}
                />
              </div>
            </Tooltip>
            <Tooltip placement="top" content="Right Align">
              <div>
                <Property.Toggle
                  icon={faAlignRight}
                  active={batchMobileTextStyle.alignment === "right"}
                  onChange={() => handleBatchAlignmentChange("right")}
                />
              </div>
            </Tooltip>
            <Tooltip placement="top" content="Justify">
              <div>
                <Property.Toggle
                  icon={faAlignJustify}
                  active={batchMobileTextStyle.alignment === "justify"}
                  onChange={() => handleBatchAlignmentChange("justify")}
                />
              </div>
            </Tooltip>
          </div>
        </Property>
        
        <Font.Spacing
          value={{
            lineSpacing: batchMobileTextStyle.lineSpacing,
            letterSpacing: batchMobileTextStyle.letterSpacing,
          }}
          onChange={handleBatchSpacingChange}
        />
      </Property.Body>
    </Property.Dialog>
  );
};

const TextVariableDialogue = ({
  widget,
  editorSession,
  disabled,
  onCancel,
  fields,
  textEditor,
}) => {
  const dispatch = useDispatch();

  const present = useSelector((state) =>
    _.get(state, `${editorSession}.present`)
  );
  const widgets = _.get(present, "widgets");
  var selectedWidget = widgets.find((item) => item.uid === widget.uid);
  var widgetVariables =
    _.get(selectedWidget, "properties.desktop.variables", {}) || {};
  const variableKeys = Array.isArray(widgetVariables)
    ? widgetVariables
    : Object.keys(widgetVariables);

  const createTextVariable = (requestVariable) => {
    if (!textEditor.selection.isCollapsed() && selectedWidget) {
      const request = {
        ...selectedWidget.properties.desktop,
        variables: [...variableKeys, requestVariable.variable],
      };

      dispatch(
        updateWidgetProperty(editorSession, widget.uid, "desktop", request)
      );

      textEditor.plugins.variable.addVariable(requestVariable.data);
    }
  };

  return (
    <VariableProperty
      widget={widget}
      disabled={disabled}
      onCancel={onCancel}
      fields={fields}
      onConfirm={createTextVariable}
    />
  );
};

const TextMobileStyleEditor = ({ position, editorSession, ...rest }) => {
  const dispatch = useDispatch();

  const fontAlignment = _.get(rest, `properties.mobile.alignment`);
  const fontSize = _.get(rest, `properties.mobile.fontSize`);
  const lineSpacing = _.get(rest, `properties.mobile.lineSpacing`) || 1.2;
  const letterSpacing = _.get(rest, `properties.mobile.letterSpacing`) || 0;

  const spacing = {
    lineSpacing,
    letterSpacing,
  };

  const handleSizeChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, rest.uid, "mobile", {
        fontSize: value,
      })
    );
  };

  const handleAlignmentChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, rest.uid, "mobile", {
        alignment: value,
      })
    );
  };

  const handleSpacingChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, rest.uid, "mobile", {
        ...value,
      })
    );
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, rest.uid, false));
  };

  return (
    <Property.Dialog
      key={rest.uid}
      className={cx(styles.fontDialog, styles.mobile)}
      width={222}
      target={`#${rest.uid}`}
      title="Text Settings"
      onCancel={closePopup}
    >
      <Property.Body>
        <Font.Size
          min={6}
          max={150}
          value={fontSize}
          onChange={handleSizeChange}
        />
        <Property name="Alignment">
          <div className={styles.alignmentActions}>
            <Tooltip placement="top" content="Left Align">
              <div>
                <Property.Toggle
                  icon={faAlignLeft}
                  active={fontAlignment === "left"}
                  onChange={() => handleAlignmentChange("left")}
                />
              </div>
            </Tooltip>
            <Tooltip placement="top" content="Center Align">
              <div>
                <Property.Toggle
                  icon={faAlignCenter}
                  active={fontAlignment === "center"}
                  onChange={() => handleAlignmentChange("center")}
                />
              </div>
            </Tooltip>
            <Tooltip placement="top" content="Right Align">
              <div>
                <Property.Toggle
                  icon={faAlignRight}
                  active={fontAlignment === "right"}
                  onChange={() => handleAlignmentChange("right")}
                />
              </div>
            </Tooltip>
            <Tooltip placement="top" content="Justify">
              <div>
                <Property.Toggle
                  icon={faAlignJustify}
                  active={fontAlignment === "justify"}
                  onChange={() => handleAlignmentChange("justify")}
                />
              </div>
            </Tooltip>
          </div>
        </Property>
        <Font.Spacing value={spacing} onChange={handleSpacingChange} />
      </Property.Body>
    </Property.Dialog>
  );
};

export const TextLinkAction = ({ id, textEditor, editorSession, onChange }) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);

  const getLink = (textEditor, node) => {
    if (!node) {
      return null;
    }

    if (node.tagName === "A") {
      return node;
    }

    var partialSelection =
      node.textContent != textEditor.selection?.getContent();
    if (partialSelection) {
      return;
    }

    var linkElement = textEditor.dom?.getParent(node, "a[data-action]");
    if (linkElement) {
      return linkElement;
    }

    const children = Array.from(node.children);
    if (children.length == 1) {
      const firstElement = children[0];
      if (firstElement.tagName === "A") {
        return firstElement;
      }
    }

    return null;
  };

  var selectedContent = textEditor.selection?.getNode();
  const linkElement = getLink(textEditor, selectedContent);
  const actionId = linkElement ? linkElement.dataset.action : null;
  const disabled = textEditor.selection?.isCollapsed() && actionId == null;

  const actions = useSelector((state) => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    const widget = widgets.find((item) => item.uid === id);

    return _.get(widget, "actions") || [];
  });

  const action = actionId && actions.find((action) => action.uid === actionId);

  const handleLink = () => {
    if (linkElement) {
      textEditor.selection?.select(linkElement);
    }

    setShow(true);
  };

  const handleChange = (value) => {
    const existingID = _.get(action, "uid");
    const uid = _.get(value, "uid");
    const url = _.get(value, "value") || "#";

    var updated = [...actions];
    if (existingID) {
      updated = updated.filter((item) => item.uid != existingID);
    }

    if (value) {
      updated = [...updated, value];
      dispatch(updateWidgetActions(editorSession, id, updated));
      if (textEditor.bodyElement) {
        textEditor.execCommand("mceInsertLink", false, {
          href: "" + url,
          "data-action": uid,
        });
      }
    } else {
      dispatch(updateWidgetActions(editorSession, id, updated));
      if (textEditor.bodyElement) {
        textEditor.execCommand("unlink");
      }
    }
  };

  return (
    <>
      <Tooltip placement="top" content="Insert Link">
        <Button
          variant="info"
          id={`${id}_link_property`}
          className={styles.linkAction}
          disabled={disabled}
          onClick={handleLink}
        >
          <i className={"cleverstory-link"} />
        </Button>
      </Tooltip>

      {show && (
        <Actions
          target={`${id}_link_property`}
          editorSession={editorSession}
          value={action}
          onChange={handleChange}
          onCancel={() => {
            setShow(false);
          }}
        />
      )}
    </>
  );
};

const getStyles = (widget, breakpoint) => {
  const fontAlignment = _.get(widget, `properties.${breakpoint}.alignment`);
  const fontSize = _.get(widget, "properties.desktop.fontSize") || 16;
  const lineSpacing =
    _.get(widget, `properties.${breakpoint}.lineSpacing`) || 1.2;
  const letterSpacing =
    _.get(widget, `properties.${breakpoint}.letterSpacing`) || 0;

  const width = _.get(widget, `properties.${breakpoint}.layout.width`);
  const autoWidth = width === "auto";

  var widgetStyle = {
    textAlign: fontAlignment,
    lineHeight: `${lineSpacing}`,
    letterSpacing: `${fontSize * letterSpacing}px`,
    overflowWrap: "break-word",
    whiteSpace: autoWidth && "nowrap",
  };

  return widgetStyle;
};

const removeEmptySpan = (str) => {
  var dom = document.createElement("div");
  dom.innerHTML = str;
  const emptySpans = dom.querySelectorAll("span:empty");

  emptySpans.forEach((span) => {
    span.parentNode.removeChild(span);
  });

  return dom.outerHTML;
};

TextWidget.sizeConfig = (widget) => {
  const text = _.get(widget, "content");

  const estimatedSizeBytes = (text?.length || 1) * 1.5;

  // Convert bytes to kilobytes and megabytes
  const sizeKB = estimatedSizeBytes / 1024;
  const sizeMB = sizeKB / 1024;

  return {
    size: sizeMB,
    type: "text",
  };
};

TextWidget.processRequest = (widget) => {
  var content = removeEmptySpan(_.get(widget, "content"));
  var actions = _.get(widget, "actions", []) || [];

  const actionIds = getActionsFromHTML(content) || [];
  let updated = actions.filter((action) => _.includes(actionIds, action.uid));

  updated = (updated || []).map((action) => {
    if (action.type == "asset") {
      const value =
        _.get(action, "value.content.id") || _.get(action, "value", null);
      return {
        ...action,
        value: value,
      };
    }
    return action;
  });

  const fonts = _.get(widget, "fonts", []) || [];

  const variables = _.get(widget, "properties.desktop.variables") || [];

  return {
    ...widget,
    actions: updated,
    fonts: fonts,
    properties: {
      ...widget.properties,
      desktop: {
        ..._.get(widget, "properties.desktop"),
        variables: Array.isArray(variables) ? variables : Object.keys(variables),
      },
    },
  };
};

TextWidget.moveableConfig = (editorSession, widget) => {
  const contentCard = getParentContainerOfType(
    editorSession,
    widget.uid,
    CONTENT_CARD
  );
  var bounds = null;
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }

  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    bounds: bounds,
    renderDirections: ["e", "w"],
    onReparent: (widget, parent) => {
      const parentWidget = getParentContainerOfType(
        editorSession,
        widget,
        CONTENT_CARD
      );
      if (parentWidget || group) {
        return false;
      }

      return true;
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set("height", "auto");
      return frame.get();
    },
  };
};

export { TextWidget };
